import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, MotionConfig } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { useAdminLocations } from '@/api/opengater/useLocations';
import { Button } from '@/components/primitives/Button';
import { SegmentedControl } from '@/components/primitives/SegmentedControl';
import { Icon } from '@/components/Icon';
import { PageTitle } from '@/components/PageTitle';
import { ListRow, SettingsGroup, SettingsLoading, SettingsNote, SettingsState, } from '@/components/settings/Settings';
import { plural } from '@/lib/plural';
import { parseLocationView, setLocationView } from './locations/locationUrl';
import { LocationPanel } from './locations/LocationPanel';
import { LocationDirectories } from './locations/LocationDirectories';
import { LocationCreateForm } from './locations/LocationCreateForm';
import s from './OpengaterLocations.module.css';
function sectionsOf(rows) {
    const sections = [];
    for (const row of rows) {
        const key = row.group ? `group-${row.group.id}` : 'ungrouped';
        const previous = sections.at(-1);
        if (!previous || previous.key !== key) {
            sections.push({ key, title: row.group?.name ?? 'Без группы', rows: [row] });
        }
        else {
            previous.rows.push(row);
        }
    }
    return sections;
}
function usersText(count) {
    if (count === 0)
        return 'Нет пользователей';
    return `${count.toLocaleString('ru-RU')} ${plural(count, 'пользователь', 'пользователя', 'пользователей')}`;
}
export function OpengaterLocations() {
    const [status, setStatus] = useState('active');
    const [notice, setNotice] = useState(null);
    const locations = useAdminLocations(status);
    const [searchParams, setSearchParams] = useSearchParams();
    const view = useMemo(() => parseLocationView(searchParams), [searchParams]);
    const sections = useMemo(() => sectionsOf(locations.data ?? []), [locations.data]);
    useEffect(() => {
        if (!view.invalidKeys.length)
            return;
        const next = new URLSearchParams(searchParams);
        for (const key of view.invalidKeys)
            next.delete(key);
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, view.invalidKeys]);
    const openLocation = (id) => {
        setNotice(null);
        setSearchParams(setLocationView(searchParams, { locationId: id }));
    };
    const openDirectories = () => {
        setNotice(null);
        setSearchParams(setLocationView(searchParams, { directory: 'options' }));
    };
    const openCreateLocation = () => {
        setNotice(null);
        setSearchParams(setLocationView(searchParams, { create: 'location' }));
    };
    const navigatePanel = (patch, options) => setSearchParams(setLocationView(searchParams, patch), options);
    const closePanel = () => setSearchParams(view.directory != null
        ? setLocationView(searchParams, { directory: null })
        : setLocationView(searchParams, { locationId: null }));
    const finishCreateLocation = (id, isActive) => {
        if (!isActive)
            setStatus('all');
        setNotice(id == null ? 'Локация создана. Откройте её из обновлённого списка.' : null);
        const parent = setLocationView(searchParams, { create: null });
        setSearchParams(id == null ? parent : setLocationView(parent, { locationId: id }), {
            replace: true,
        });
    };
    const closeCreateLocation = () => setSearchParams(setLocationView(searchParams, { create: null }), { replace: true });
    const panelOpen = view.locationId != null || view.directory != null || view.create != null;
    return (_jsxs("div", { className: s.page, children: [_jsxs(PageTitle, { title: "\u041B\u043E\u043A\u0430\u0446\u0438\u0438", sub: "\u0422\u043E\u0447\u043A\u0438 \u043F\u043E\u0434\u043A\u043B\u044E\u0447\u0435\u043D\u0438\u044F, \u043E\u043F\u0446\u0438\u0438 \u0438 \u0441\u043A\u0432\u0430\u0434\u044B", children: [_jsxs(Button, { type: "button", onClick: openCreateLocation, children: [_jsx(Icon, { name: "plus", size: 15 }), " \u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u043B\u043E\u043A\u0430\u0446\u0438\u044E"] }), _jsxs(Button, { type: "button", variant: "ghost", onClick: openDirectories, children: [_jsx(Icon, { name: "sliders", size: 15 }), " \u0421\u043F\u0440\u0430\u0432\u043E\u0447\u043D\u0438\u043A\u0438"] })] }), _jsx("div", { className: s.toolbar, children: _jsx(SegmentedControl, { value: status, onChange: setStatus, options: [
                        { value: 'active', label: 'Активные' },
                        { value: 'hidden', label: 'Скрытые' },
                        { value: 'all', label: 'Все' },
                    ] }) }), notice && _jsx(SettingsNote, { tone: "ok", children: notice }), locations.isLoading && _jsx(SettingsLoading, {}), locations.error && !locations.isLoading && (_jsx(SettingsNote, { tone: "error", children: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u043B\u043E\u043A\u0430\u0446\u0438\u0438." })), !locations.isLoading && !locations.error && sections.length === 0 && (_jsx(SettingsState, { children: "\u041B\u043E\u043A\u0430\u0446\u0438\u0439 \u0441 \u0442\u0430\u043A\u0438\u043C \u0441\u0442\u0430\u0442\u0443\u0441\u043E\u043C \u043D\u0435\u0442" })), sections.length > 0 && (_jsx("section", { className: s.list, "aria-label": "\u041B\u043E\u043A\u0430\u0446\u0438\u0438", children: sections.map((section) => (_jsx(SettingsGroup, { title: section.title, children: section.rows.map((row) => (_jsx(ListRow, { leading: _jsx("span", { className: s.flag, children: row.flag || '—' }), title: row.name, sub: [
                            row.description,
                            row.is_premium ? 'Premium' : null,
                            row.speed != null ? `${row.speed} Мбит/с` : null,
                        ]
                            .filter(Boolean)
                            .join(' · '), value: _jsxs("span", { className: s.status, "data-hidden": !row.is_active || undefined, children: [!row.is_active && 'Скрыта · ', usersText(row.users_count)] }), dim: !row.is_active, active: view.locationId === row.id, onOpen: () => openLocation(row.id) }, row.id))) }, section.key))) })), _jsx(MotionConfig, { reducedMotion: "user", children: _jsxs(AnimatePresence, { children: [view.create === 'location' && (_jsx(LocationCreateForm, { onCreated: finishCreateLocation, onClose: closeCreateLocation }, "location-create")), view.locationId != null && (_jsx(LocationPanel, { locationId: view.locationId, view: view, onNavigate: navigatePanel, onClose: closePanel }, "location-card")), view.locationId == null && view.directory != null && panelOpen && (_jsx(LocationDirectories, { view: view, onNavigate: navigatePanel, onClose: closePanel }, "location-directories"))] }) })] }));
}
